import {useMemo} from 'react';

import {isDefined, isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {PageData} from 'PayloadUI/dataSelectors/getPageData';

/**
 * Generates meta information and structured data used for SEO, social sharing, and canonical links.
 * This hook processes the provided `pageMetaData` object and returns normalized values for meta tags
 * such as canonical URL, page title, preview image, and structured data for JSON-LD output.
 * It is typically used in layout or `<Head>` components to populate Open Graph, Twitter Card,
 * and schema.org metadata.
 * If the tenant is fully defined, it constructs a base URL for absolute links and combines it with
 * the page slug to form a complete canonical URL. It also generates `BreadcrumbList` and `LocalBusiness`
 * JSON-LD schemas based on tenant and folder data.
 *
 * @param meta             The full page metadata including SEO fields, tenant info, and optional breadcrumbs.
 * @param meta.seo         SEO-relevant data including title, slug, canonical URL, image, etc.
 * @param meta.tenant      Optional website context used to build full URLs and schema.org organization data.
 * @param meta.breadcrumbs Optional folder hierarchy used to build breadcrumb schema.
 * @returns An object containing normalized values: `canonicalUrl`, `image`, `structuredData`, `title`, and `url`.
 *
 * @example
 * ```tsx
 * const { canonicalUrl, title, image, structuredData } = useMetaData(pageMetaData);
 * ```
 */
export const useMetaData = (meta?: PageData['pageMetaData']) => useMemo(() => {
    const {
        canonical: seoCanonical,
        description: seoDescription,
        image: seoImage,
        keywords: seoKeywords,
        slug: seoSlug,
        title: seoTitle
    } = meta?.seo ?? {};

    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const url = baseUrl && seoSlug ? `${baseUrl}${seoSlug}` : '';
    const canonicalUrl = seoCanonical ?? url;
    const title = seoTitle ?? 'Glasfaser für München';
    const image = isDefinedAndPopulated(seoImage) && isDefined(seoImage.url) ? `${baseUrl}${seoImage.sizes?.sm?.url ?? seoImage.url}` : undefined;
    const desc = seoDescription;
    const keywords = seoKeywords;

    return {
        canonicalUrl,
        desc,
        image,
        keywords,
        title,
        url
    };
}, [meta]);